/**
 * Small page-level building blocks shared by the pages outside a mission (login, onboarding, shoots,
 * settings). They compose the design-system primitives; they are not primitives themselves.
 */
import type { ReactNode, Ref } from 'react';
import { Link } from 'react-router';
import { BrandMark, cx, Icon, type IconName } from '../../components/ui';
import { useSession } from '../../state/session';
import { AppHeader } from '../AppHeader';

/** A disclosure with the product chevron instead of the browser's ▶ triangle. Stays a native
 *  <details>/<summary> so it is keyboard- and screen-reader-native and e2e can open it by summary. */
export function Disclosure({
  summary,
  meta,
  children,
  className,
  open,
  variant = 'plain',
  detailsRef,
}: {
  summary: ReactNode;
  /** Quiet text after the summary (state, count…). */
  meta?: ReactNode;
  children: ReactNode;
  className?: string;
  open?: boolean;
  /** `card`: a bordered row that expands in place; `plain`: an inline text disclosure. */
  variant?: 'plain' | 'card';
  detailsRef?: Ref<HTMLDetailsElement>;
}) {
  return (
    <details className={cx('disclosure', `disclosure--${variant}`, className)} open={open} ref={detailsRef}>
      <summary className="disclosure__summary">
        <span className="disclosure__label">
          {summary}
          {meta ? <span className="disclosure__meta"> {meta}</span> : null}
        </span>
        <Icon name="chevron-down" size={16} className="disclosure__chev" />
      </summary>
      <div className="disclosure__body">{children}</div>
    </details>
  );
}

export type NoticeTone = 'neutral' | 'success' | 'warning' | 'error';
const NOTICE_ICON: Record<NoticeTone, IconName> = { neutral: 'info', success: 'check', warning: 'alert', error: 'alert' };

/** An inline callout: icon well, title, body and an optional action. Neutral is the default; colour
 *  only when the state means something (error, success, a real warning). */
export function Notice({
  tone = 'neutral',
  icon,
  title,
  children,
  action,
  className,
  role,
  testId,
}: {
  tone?: NoticeTone;
  icon?: IconName;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  role?: 'status' | 'alert';
  testId?: string;
}) {
  return (
    <div className={cx('notice', `notice--${tone}`, className)} role={role} data-testid={testId}>
      <span className="notice__icon" aria-hidden>
        <Icon name={icon ?? NOTICE_ICON[tone]} size={16} />
      </span>
      <div className="notice__text">
        {title ? <div className="notice__title">{title}</div> : null}
        {children ? <div className="notice__body">{children}</div> : null}
      </div>
      {action ? <div className="notice__action">{action}</div> : null}
    </div>
  );
}

/** Header for public pages (privacy, support, not found): the app header when signed in, a lean
 *  brand bar with a sign-in link when not. */
export function PageHeader({ back }: { back?: { to: string; label: string } }) {
  const user = useSession((s) => s.user);
  if (user) return <AppHeader back={back} />;
  return (
    <header className="app-header">
      <div className="app-header__left">
        <Link to="/login" className="brand-lockup" aria-label="Airview Studio — sign in">
          <BrandMark size={22} />
          <span className="brand-lockup__word">AIRVIEW STUDIO</span>
        </Link>
      </div>
      <div className="app-header__right">
        <Link to="/login" className="btn btn--secondary btn--sm">
          <span className="btn__label">Sign in</span>
        </Link>
      </div>
    </header>
  );
}
